import clsx from 'clsx'
import React, {useMemo} from 'react'
import styles from './StatsTable.module.scss'
import {TranslationObject} from '@colonist/i18n'
import {Tooltip} from '../../../../common/Tooltip'
import {T} from '../../../../common/T'
import IconHighlightCircle from '@static/images/icon_highlight_circle.svg'
import {ImageURL} from '@colonist/types'

export interface StatsTableProps {
    className?: string
    headers: {
        imagePath: ImageURL
        tooltip: TranslationObject
        onClick?: VoidFunction
    }[]
    enableHighlight?: boolean
    rows: number[][]
    rowProps?: Record<number, {
        rowRenderer?: (value: number) => React.ReactNode
        disableRowHighlight?: boolean
    }>
}

export const StatsTable = ({
    className,
    headers,
    rows,
    enableHighlight,
    rowProps,
}: StatsTableProps) => {
    const highlightIndices = useMemo(() => {
        if(rows.length === 0) {
            return []
        }
        const columnCount = headers.length
        const initial = Array(columnCount).fill(Number.MIN_SAFE_INTEGER)

        const maxValues = rows.reduce((acc, row) => {
            row.forEach((value, colIndex) => {
                if(value > acc[colIndex]) {
                    acc[colIndex] = value
                }
            })
            return acc
        }, initial)

        return maxValues.map((maxValue, colIndex) => rows.findIndex((row) => row[colIndex] === maxValue))
    }, [rows])

    return (
        <div className={clsx(styles.container, className)}>
            <div className={styles.headerContainer}>
                {headers.map((header, index) => (
                    <Tooltip centerText key={index} title={<T id={header.tooltip}/>}>
                        <img
                            className={clsx(styles.headerIcon, {[styles.clickable]: header.onClick})}
                            key={index}
                            onClick={header.onClick}
                            src={header.imagePath}
                        />
                    </Tooltip>
                ))}
            </div>

            {rows.map((values, rowIndex) => (
                <div className={styles.rowContainer} key={rowIndex}>
                    {values.map((value, colIndex) => {
                        const shouldHighlight = enableHighlight && rowIndex === highlightIndices[colIndex]
                        const displayValue = value ?? 0
                        return (
                            <div
                                className={styles.valueContainer}
                                key={colIndex}
                            >
                                {shouldHighlight && !rowProps?.[colIndex]?.disableRowHighlight && (
                                    <img
                                        className={styles.highlight}
                                        src={IconHighlightCircle}
                                    />
                                )}
                                <div className={styles.value}>
                                    {rowProps?.[colIndex]?.rowRenderer ? rowProps?.[colIndex]?.rowRenderer?.(value) : displayValue}
                                </div>
                            </div>
                        )
                    })}
                </div>
            ))}
        </div>
    )
}
